import React from 'react';
import { Table, TableColumnsType, Tag } from 'antd';
import { ColumnType, ColumnsType, TablePaginationConfig } from 'antd/es/table';
import FilterSolid from '../../assets/icons/filter-solid-b.svg';
import { useAppDispatch, useAppSelector } from '../../store';
import { IProps } from './type';
import styles from './index.module.scss';
import { tableActions } from '../../store/reducers/tableReducer';
import { FilterValue } from 'antd/es/table/interface';
import { status_colors } from '../../assets/conts';
import { getValueFromEnum } from '../../utils/functions';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import SDropdown from '../FormElements/SDropdown';
import globalStyle from '../../assets/styles/index.module.scss';
import SInput from '../FormElements/SInput';

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

const DataTable = <T extends object>({
  dataSource,
  columns,
  scrollable,
  expandedRows,
  isPaginationDisplayed = true,
  isFooterDisplayed = true,
  statusChoices,
  isSticky = true,
}: IProps<T>) => {
  const dispatch = useAppDispatch();
  const row = useAppSelector((state) => state.tableReducer.row);
  const total = useAppSelector((state) => state.tableReducer.total);
  const page = useAppSelector((state) => state.tableReducer.page);

  const locale = {
    triggerAsc: 'Sıralamak için tıklayın',
    triggerDesc: 'Sıralamak için tıklayın',
    cancelSort: 'Sıralamak için tıklayın',
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: any,
  ) => {
    const filterArray = Object.entries(filters)
      .filter(([_, value]) => value && value.length > 0)
      .flatMap(([key, value]) =>
        (value as string[]).map((val) => ({
          key,
          value: val,
        })),
      );

    dispatch(tableActions.changeTableSearch(filterArray));

    if (!sorter.order) {
      dispatch(tableActions.clearTableSort());
    } else {
      dispatch(
        tableActions.changeTableSort({
          key: sorter.field?.toString() || '',
          order: sorter.order,
        }),
      );
    }

    if (pagination.current) {
      dispatch(
        tableActions.changeTablePage({
          size: pagination.pageSize || 10,
          number: pagination.current - 1 || 0,
        }),
      );
    }
  };

  const getColumnSearchProps = (
    column: ColumnType<T> & { searchable?: boolean | undefined },
  ): ColumnsType<T>[number] => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
      <div className={styles.inputContainer}>
        {column.key === 'organization' ? (
          <div style={{ padding: 8 }}>
            <SDropdown
              showSearch
              className={globalStyle.width200px}
              placeholder="Şirket seçin"
              optionFilterProp="children"
              value={selectedKeys[0] || null}
              options={[
                { value: '', label: 'Tümü' },
                ...(column.filters?.map((item) => ({
                  value: item.value.toString(),
                  label: item.text,
                })) || []),
              ]}
              onChange={(value) => {
                setSelectedKeys(value ? [value] : []);
                confirm();
              }}
            />
          </div>
        ) : (
          <SInput
            placeholder={`${column.title} ara`}
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
          />
        )}
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <span
        style={{
          width: 14,
          height: 14,
          display: 'inline-block',
          WebkitMaskImage: `url(${FilterSolid})`,
          maskImage: `url(${FilterSolid})`,
          WebkitMaskRepeat: 'no-repeat',
          maskRepeat: 'no-repeat',
          WebkitMaskPosition: 'center',
          maskPosition: 'center',
          WebkitMaskSize: 'contain',
          maskSize: 'contain',
          backgroundColor: filtered ? '#1677ff' : '#999',
          transition: 'background-color 120ms ease',
        }}
      />
    ),
  });

  const enhancedColumns = columns.map((col) => {
    if (col.dataIndex) {
      return {
        ...col,
        ...(col.searchable ? getColumnSearchProps(col) : {}),
      };
    }
    return col;
  });

  interface ExpandedDataType {
    status: string;
    created_at: string;
    created_by: string;
    user_organization: string;
  }

  const expandedRowRender = () => {
    const columns: TableColumnsType<ExpandedDataType> = [
      {
        title: 'Şirket',
        dataIndex: 'user_organization',
        key: 'user_organization',
        sorter: (a, b) =>
          a.user_organization?.localeCompare(b.user_organization),
      },
      {
        title: 'İşlem Sahibi',
        dataIndex: 'created_by',
        key: 'created_by',
        sorter: (a, b) => a.created_by?.localeCompare(b.created_by),
      },
      {
        title: 'İşlem Tarihi',
        dataIndex: 'created_at',
        key: 'created_at',
        sorter: (a, b) => a.created_at?.localeCompare(b.created_at),
        render: (text: string) => (
          <div>
            {dayjs
              .utc(text, 'DD-MM-YYYYTHH:mm:ss.SSS[Z]')
              .tz('Europe/Istanbul')
              .format('YYYY-MM-DD HH:mm:ss')}
          </div>
        ),
      },
      {
        title: 'İşlem Detayı',
        dataIndex: 'status',
        key: 'status',
        render: (value) => (
          <Tag className={styles.tag} color={status_colors[value]}>
            {statusChoices && getValueFromEnum(statusChoices, value)}
          </Tag>
        ),
      },
    ];

    return (
      <Table
        locale={locale}
        rootClassName={styles.subTable}
        columns={columns}
        dataSource={expandedRows}
        pagination={false}
      />
    );
  };

  return (
    <Table
      rootClassName={styles.table}
      dataSource={dataSource}
      columns={enhancedColumns}
      locale={locale}
      sticky={isSticky}
      scroll={{
        y: 'calc(100vh - 420px)',
        x: scrollable ? 'max-content' : undefined,
      }}
      expandable={{
        expandedRowRender,
        expandedRowKeys: row !== 0 ? [row] : [],
        expandIcon: () => null,
        showExpandColumn: false,
      }}
      pagination={
        isPaginationDisplayed
          ? {
              total,
              pageSize: page.size,
              current: page.number + 1,
              showSizeChanger: true,
              pageSizeOptions: ['5', '10', '20', '50', '100'],
            }
          : false
      }
      footer={() => (
        <>
          {isFooterDisplayed && (
            <div className={globalStyle.dataTableFooter}>
              Toplam {total} kayıt listelenmektedir.
            </div>
          )}
        </>
      )}
      onChange={handleTableChange}
    />
  );
};

export default DataTable;
